/**
 * geoInfo.js
 *
 * Provides user country and timezone information using:
 * 1. `countries-and-timezones` for instant offline lookup from IANA timezone.
 * 2. IP Geolocation API (ipapi.co) as a more accurate async alternative.
 *
 * Handles IANA aliases automatically (e.g., Asia/Calcutta → Asia/Kolkata → IN / India).
 */
import ct from 'countries-and-timezones';

/**
 * Synchronously get geo info from IANA timezone (no network needed).
 * Best for: UI display, dropdown defaults, quick rendering.
 */
export function getGeoFromTimezone() {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
        const tzData = ct.getTimezone(tz); // resolves aliases
        const countryCode = tzData?.countries?.[0] ?? null;
        const country = countryCode ? ct.getCountry(countryCode) : null;

        return {
            timezone: tz,
            countryCode: countryCode ?? 'UNKNOWN',
            countryName: country?.name ?? 'Unknown',
        };
    } catch {
        return { timezone: tz, countryCode: 'UNKNOWN', countryName: 'Unknown' };
    }
}

/**
 * Asynchronously get geo info from the user's IP address.
 * More accurate for multi-timezone countries (e.g., US, AU, RU).
 * Falls back to timezone-based detection if the request fails.
 *
 * Best for: Auth session logging, audit trails.
 */
export async function getGeoFromIP() {
    try {
        const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(4000) });
        if (!res.ok) throw new Error('ipapi failed');
        const data = await res.json();
        return {
            ip: data.ip ?? null,
            timezone: data.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
            countryCode: data.country_code ?? 'UNKNOWN',
            countryName: data.country_name ?? 'Unknown',
            city: data.city ?? null,
        };
    } catch {
        // Graceful fallback to offline timezone-based detection
        const fallback = getGeoFromTimezone();
        return { ip: null, city: null, ...fallback };
    }
}
